/**
 * The "it's live" celebration (17 Oct 2026). When you move a task to Live, a burst of flame-coloured confetti (21 Oct
 * 2026: no card or picture any more, and off until someone turns it on in the account menu, kept in this browser).
 * Only for the person who moved it, once per action (a bulk move of many tasks is one burst), never for moves made by
 * others or over MCP. People who ask their system for less motion get no confetti; screen readers hear the message.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

/** How long the confetti stays (Esc puts it away sooner). */
const SHOW_MS = 3200;

const EVENT = 'tf-celebrate';
const PREF = 'tf-celebrations';

/** True when this person has switched celebrations on (off by default, 20 Oct 2026). */
export function celebrationsOn(): boolean {
  try {
    return localStorage.getItem(PREF) === 'on';
  } catch {
    return false;
  }
}
export function setCelebrations(on: boolean) {
  try {
    if (on) localStorage.setItem(PREF, 'on');
    else localStorage.removeItem(PREF);
  } catch {
    // Not kept; the choice holds for this visit only.
  }
}

/** The status that counts as "it went live". */
export const isLive = (s: { name: string } | null | undefined) => !!s && s.name.trim().toLowerCase() === 'live';

/** After a move succeeded: dance if it was to Live. `what` names the task(s), e.g. "BW-12" or "3 tasks". */
export function celebrateIfLive(status: { name: string } | null | undefined, what: string) {
  if (!isLive(status) || !celebrationsOn()) return;
  window.dispatchEvent(new CustomEvent(EVENT, { detail: { what } }));
}

/** Wraps a status move: celebrates once it has gone through. */
export function liveMove<T>(req: Promise<T>, status: { name: string } | null | undefined, what: string): Promise<T> {
  return req.then((r) => {
    celebrateIfLive(status, what);
    return r;
  });
}

const reducedMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

/** Mounted once in Task Force; shows the celebration when asked. */
export function Celebration({ firstName }: { firstName?: string }) {
  const [what, setWhat] = useState<string | null>(null);
  const [n, setN] = useState(0);
  const close = useCallback(() => setWhat(null), []);
  useEffect(() => {
    const on = (e: Event) => {
      setWhat((e as CustomEvent<{ what: string }>).detail.what);
      setN((x) => x + 1);
    };
    window.addEventListener(EVENT, on);
    return () => window.removeEventListener(EVENT, on);
  }, []);
  useEffect(() => {
    if (!what) return;
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && close();
    document.addEventListener('keydown', esc);
    const t = setTimeout(close, SHOW_MS);
    return () => {
      document.removeEventListener('keydown', esc);
      clearTimeout(t);
    };
  }, [what, close]);
  if (!what) return null;
  const still = reducedMotion();
  return (
    <div className="tfparty" role="status" aria-live="polite" key={n}>
      {!still && <Confetti />}
      <span className="visually-hidden">
        {what} is live! {firstName ? `Nice work, ${firstName}.` : 'Nice work.'}
      </span>
    </div>
  );
}

/** A short burst of flame-coloured confetti across the window, drawn on a canvas. */
function Confetti() {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const c = ref.current;
    const ctx = c?.getContext('2d');
    if (!c || !ctx) return;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const W = window.innerWidth;
    const H = window.innerHeight;
    c.width = W * dpr;
    c.height = H * dpr;
    ctx.scale(dpr, dpr);
    const colors = ['#e00000', '#ff5a1f', '#ff9f1a', '#ffd23f', '#ffffff', '#1b1c1f'];
    // Two cannons from the bottom corners.
    const bits = Array.from({ length: 160 }, (_, i) => {
      const left = i % 2 === 0;
      const angle = -Math.PI / 2 + (left ? 0.45 : -0.45) + (Math.random() - 0.5) * 0.7;
      const speed = 9 + Math.random() * 9;
      return {
        x: left ? 0 : W,
        y: H,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        r: 4 + Math.random() * 5,
        spin: Math.random() * Math.PI,
        vs: (Math.random() - 0.5) * 0.3,
        color: colors[i % colors.length]!,
      };
    });
    const start = performance.now();
    let last = start;
    let raf = 0;
    const frame = (now: number) => {
      const t = now - start;
      // Counted in 60 Hz frames, so the burst plays at the same speed on a 120 Hz screen (22 Oct 2026).
      const k = Math.min(3, (now - last) / (1000 / 60));
      last = now;
      ctx.clearRect(0, 0, W, H);
      ctx.globalAlpha = Math.max(0, 1 - Math.max(0, t - 2200) / 800);
      for (const b of bits) {
        b.vy += 0.28 * k;
        b.vx *= 0.99 ** k;
        b.x += b.vx * k;
        b.y += b.vy * k;
        b.spin += b.vs * k;
        ctx.save();
        ctx.translate(b.x, b.y);
        ctx.rotate(b.spin);
        ctx.fillStyle = b.color;
        ctx.fillRect(-b.r / 2, -b.r / 4, b.r, b.r / 2);
        ctx.restore();
      }
      if (t < 3000) raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, []);
  return <canvas ref={ref} className="tfparty-confetti" aria-hidden="true" />;
}
